import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Getting started with Paddiq on the web | Paddiq';

export function GettingStartedPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Getting started</p>
            <h1>Set up your first Paddiq session.</h1>
            <p className="guide-hero__lead">
              Add a driver and vehicle, upload a MyChron session, and make sure the run is ready to
              review.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/register')}
              >
                Create your workspace
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href={appPath('/login')}
              >
                Log in to Paddiq
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/getting-started" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#access">Finish workspace access</a>
                </li>
                <li>
                  <a href="#driver">Add a driver</a>
                </li>
                <li>
                  <a href="#vehicle">Add a vehicle</a>
                </li>
                <li>
                  <a href="#upload">Upload a session</a>
                </li>
                <li>
                  <a href="#review">Review the run</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you start</strong>
                <span>
                  Have a supported MyChron file ready if you want to complete the full workflow.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="access">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Workspace access</p>
                  <h2>Finish the prompts shown after sign-in.</h2>
                  <p>
                    Sign in and complete any workspace, access, or billing prompt shown in Paddiq.
                    The actions available to you depend on your role and the workspace's current
                    access.
                  </p>
                  <p>
                    If you joined someone else's team, the team owner may need to approve your
                    access or create records for you before you can continue.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="driver">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Team</p>
                  <h2>Add your first driver.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Team</strong> in the main navigation.
                    </li>
                    <li>
                      If your role can manage accounts, select <strong>Create Account</strong>.
                    </li>
                    <li>
                      Add the driver using the fields shown for your workspace. See the{' '}
                      <a className="guide-text-link" href="/resources/add-team-members">
                        drivers and team members guide
                      </a>{' '}
                      for account types, minors, and setup links.
                    </li>
                    <li>Confirm that the driver appears in Team before continuing.</li>
                  </ol>
                  <div className="guide-callout">
                    If you cannot see <strong>Create Account</strong>, ask a team owner or another
                    person with account-management access to add the driver.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="vehicle">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Garage</p>
                  <h2>Add a kart or vehicle.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Garage</strong>.
                    </li>
                    <li>
                      Select <strong>Add your first vehicle</strong> if the garage is empty, or use
                      the add action.
                    </li>
                    <li>Enter the vehicle details your team uses.</li>
                    <li>Assign the vehicle to a driver when appropriate.</li>
                  </ol>
                  <p>
                    Adding the vehicle before the first upload lets you attach equipment context to
                    the session. The Garage also keeps readiness, maintenance, and setup information
                    with the vehicle.
                  </p>
                  <p>
                    For the detailed workflow, read{' '}
                    <a className="guide-text-link" href="/resources/add-vehicle-setup">
                      Add a vehicle and record its setup
                    </a>
                    .
                  </p>
                </div>
              </section>

              <section className="guide-step" id="upload">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Sessions</p>
                  <h2>Upload a MyChron session.</h2>
                  <p>
                    To transfer sessions directly from a logger with a Mac or Windows computer,
                    follow the <a href="/resources/paddiq-link">Paddiq Link setup guide</a>. For
                    files already on your computer, use the steps below.
                  </p>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong>.
                    </li>
                    <li>
                      Select <strong>Upload</strong>.
                    </li>
                    <li>Add one or more supported files. Paddiq checks them before uploading.</li>
                    <li>
                      Confirm the circuit for each file by comparing the saved shape with the
                      recorded lap.
                    </li>
                    <li>
                      Choose the <strong>Driver</strong> and <strong>Vehicle</strong> for these
                      sessions, then select the <strong>Import</strong> button, such as{' '}
                      <strong>Import 1 session</strong>.
                    </li>
                    <li>Wait for the upload to finish, then select Open session.</li>
                  </ol>
                  <div className="guide-file-types" aria-label="Supported MyChron upload types">
                    <span>JSON</span>
                    <span>XRZ</span>
                    <span>XRK</span>
                    <span>HRZ</span>
                    <span>HRK</span>
                    <span>DRK</span>
                  </div>
                  <p>
                    The driver and vehicle choices are optional and apply to all files you selected.
                    The circuit choice is separate for each file. For a GPS-only GPX file, select{' '}
                    <strong>Uploading GPX, CSV, or another file?</strong> instead. GPX can provide a
                    trace and lap detection, but it does not contain native MyChron sensor channels.
                  </p>
                  <p>
                    Need the complete upload checklist? Read{' '}
                    <a className="guide-text-link" href="/resources/upload-mychron-session">
                      How to upload a MyChron session
                    </a>
                    .
                  </p>
                </div>
              </section>

              <section className="guide-step" id="review">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Session review</p>
                  <h2>Open the session and check the details.</h2>
                  <p>
                    Select the uploaded run in Sessions. You can review the available laps, map
                    data, telemetry channels, notes, and other context captured or added for that
                    session.
                  </p>
                  <p>
                    If Paddiq asks you to match or set up a circuit, follow the guided steps in the
                    session workspace before relying on circuit-based laps.
                  </p>
                  <p>
                    Need the full workflow? Read{' '}
                    <a className="guide-text-link" href="/resources/set-up-circuit-laps">
                      Set up circuit laps for a session
                    </a>
                    .
                  </p>
                  <div className="guide-ready">
                    <h3>Your basic setup is ready when:</h3>
                    <ul>
                      <li>the driver appears in Team;</li>
                      <li>the kart or vehicle appears in Garage;</li>
                      <li>the uploaded run appears in Sessions;</li>
                      <li>the session shows the intended driver and vehicle; and</li>
                      <li>
                        the run opens with the laps and channels available in the source file.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If something is missing</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>The Upload button is unavailable</h3>
                    <p>
                      Your role may not include upload access, or the workspace may be in a
                      billing-related read-only state. Ask the workspace owner to check the prompt
                      shown in Paddiq.
                    </p>
                  </article>
                  <article>
                    <h3>A driver or vehicle is missing</h3>
                    <p>
                      Confirm the record exists in the same workspace, refresh Sessions, and open
                      the upload form again.
                    </p>
                  </article>
                  <article>
                    <h3>The run is Unassigned</h3>
                    <p>
                      Driver and Vehicle are optional during upload. Open the existing run in{' '}
                      <strong>Session &amp; laps</strong>, expand <strong>Assignments</strong>,
                      choose the intended records, and select <strong>Save session details</strong>.
                      You do not need to upload it again.
                    </p>
                  </article>
                  <article>
                    <h3>Circuit laps are not ready</h3>
                    <p>
                      Open the circuit setup guidance in the session workspace. You may need to
                      match the run to a saved circuit or finish its lap configuration.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready for your first run?</p>
                  <h2>Bring your session into Paddiq.</h2>
                  <p>
                    Start a workspace or sign in to continue setting up the team you already joined.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/register')}
                  >
                    Create your workspace
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
